import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useCallback, useImperativeHandle, useRef, useState, useEffect } from 'react';
import useLocalization from '../hooks/useLocalization';
import useTranslation from '../hooks/useTranslation';

/**
 * ContentLightbox Component
 *
 * @param {string} headline - The headline text
 * @param {string} [size='md'] - The size of the lightbox. Available values: 'sm', 'md', 'lg', 'fullscreen'
 * @param {string} [type='boxed'] - The type of the lightbox. Available values: 'boxed', 'fluid'
 * @param {React.ReactNode} children - The content of the lightbox
 * @param {boolean} open - Whether the lightbox is open
 * @param {Function} onClose - Function to call when the lightbox is closed
 */
const ContentLightbox = ({ headline, size = 'md', type = 'boxed', children, lightboxRef }) => {
  const __ = useTranslation();
  const locale = useLocalization();
  const dialogRef = useRef(null);
  const [show, setShow] = useState(false);

  const close = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const open = useCallback(() => {
    setShow(true);
  });

  useEffect(() => {
    if (dialogRef.current && show) {
      dialogRef.current.showModal();
    } else if (dialogRef.current && !show) {
      dialogRef.current.close();
    }
  }, [show]);

  // Expose open method to the parent component
  useImperativeHandle(
    lightboxRef,
    () => ({
      open,
      close
    }),
    [open, close]
  );

  const onClickDialog = e => {
    e.preventDefault();
    const target = e.target;
    if (target.tagName === 'DIALOG') {
      close();
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.dialog
          className={`c-content-lightbox c-content-lightbox--${size} c-content-lightbox--${type}`}
          initial='hidden'
          animate='visible'
          exit='remove'
          onClick={onClickDialog}
          onCancel={e => {
            e.preventDefault();
            e.stopPropagation();
            close();
          }}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { delayChildren: 0.1 } },
            remove: {
              opacity: 0,
              transition: {
                when: 'afterChildren'
              }
            }
          }}
          layout
          role='dialog'
          ref={dialogRef}
          aria-modal='true'
        >
          <motion.div
            className='c-content-lightbox__wrapper'
            variants={{
              hidden: { y: 20, opacity: 0 },
              visible: { y: 0, opacity: 1 },
              remove: { y: 20, opacity: 0 }
            }}
          >
            {type === 'boxed' && (
              <div className='c-content-lightbox__header'>
                {headline && <p className='h3 c-content-lightbox__title'>{headline}</p>}
                <button
                  type='button'
                  title={__('close-lightbox')}
                  aria-label={__('close-lightbox')}
                  onClick={close}
                  className='c-content-lightbox__close'
                >
                  <svg viewBox='0 0 20 20' aria-hidden>
                    <use href={`${locale('svgSprite')}#close`} />
                  </svg>
                </button>
              </div>
            )}
            {type === 'fluid' && (
              <button
                type='button'
                title={__('close-lightbox')}
                aria-label={__('close-lightbox')}
                onClick={close}
                className='c-content-lightbox__close'
              >
                <svg viewBox='0 0 20 20' aria-hidden>
                  <use href={`${locale('svgSprite')}#close`} />
                </svg>
              </button>
            )}
            <div
              className={`c-content-lightbox__content u-background-${type === 'boxed' ? 'white' : 'backdrop'}`}
            >
              {children}
            </div>
          </motion.div>
        </motion.dialog>
      )}
    </AnimatePresence>
  );
};

ContentLightbox.propTypes = {
  headline: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'fullscreen']),
  type: PropTypes.oneOf(['boxed', 'fluid']),
  children: PropTypes.node,
  lightboxRef: PropTypes.shape({ current: PropTypes.instanceOf(Object) })
};

export default ContentLightbox;
